import React, {useCallback} from 'react';

import {Explanation, SampleResponse} from '../shared/ContentBlocks.react';
import {
  QuestionComponent,
  QuestionComponentProps
} from '../shared/QuestionComponent/QuestionComponent.react';

import CompletenessBanner from './CompletenessBanner.react';
import IncompletenessBanner from './IncompletenessBanner.react';
import ErrorBoundary from './WrittenSubmission/ErrorBoundary.react';
import WrittenSubmission from './WrittenSubmission/WrittenSubmission.react';

const FreeResponseQuestionComponent = ({
  actions,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  customHeader,
  displayOrientation = 'default',
  guess,
  hasQuestionSetAccessPredicate,
  headingActions,
  interactive,
  isMobile,
  modifyTitle,
  navigateBackProps,
  navigateNextProps,
  postSubmissionScrollPointRef,
  showDifficulty,
  questionState
}: QuestionComponentProps) => {
  const InputFragment = useCallback(() => {
    let writtenSubmissionFragment: React.ReactNode = null;
    if (questionState === 'PreGuess') {
      writtenSubmissionFragment = (
        <WrittenSubmission
          storeName={activeQuestionStore.getName()}
          question={activeQuestion}
          submitted={!!guess?.getId()}
        />
      );
    } else if (guess?.getId()) {
      writtenSubmissionFragment = (
        <>
          <CompletenessBanner />
          <WrittenSubmission
            storeName={activeQuestionStore.getName()}
            question={activeQuestion}
            submitted
          />
        </>
      );
    } else if (!guess?.getId()) {
      writtenSubmissionFragment = <IncompletenessBanner />;
    }

    return <ErrorBoundary>{writtenSubmissionFragment}</ErrorBoundary>;
  }, [activeQuestion, activeQuestionStore, guess, questionState]);

  const customExplanation = (
    <>
      <SampleResponse question={activeQuestion} />
      <Explanation question={activeQuestion} />
    </>
  );

  return (
    <QuestionComponent
      actions={actions}
      activeQuestion={activeQuestion}
      activeQuestionSet={activeQuestionSet}
      activeQuestionStore={activeQuestionStore}
      activeSubject={activeSubject}
      customExplanation={customExplanation}
      customHeader={customHeader}
      customHOA={<></>}
      customResultIndicator={<></>}
      displayOrientation={displayOrientation}
      guess={guess}
      hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
      headingActions={headingActions}
      interactive={interactive}
      isMobile={isMobile}
      modifyTitle={modifyTitle}
      navigateBackProps={navigateBackProps}
      navigateNextProps={navigateNextProps}
      postSubmissionScrollPointRef={postSubmissionScrollPointRef}
      questionState={questionState}
      showDifficulty={showDifficulty}
      ActiveQuestionInputArea={InputFragment}
    />
  );
};

type FreeResponseQuestionTypeProps = Omit<QuestionComponentProps, 'questionState'>;

const FreeResponseQuestion = {
  PreGuess: (props: FreeResponseQuestionTypeProps) => (
    <FreeResponseQuestionComponent {...props} questionState='PreGuess' />
  ),
  PostGuess: (props: FreeResponseQuestionTypeProps) => (
    <FreeResponseQuestionComponent {...props} questionState='PostGuess' />
  )
};

export {FreeResponseQuestion};
